import { useEffect, useState, useCallback, useMemo } from "react";
import api, { errorMessage } from "../lib/api";
import { PageHeader, PillBadge, GhostButton } from "../components/Primitives";
import { toast } from "sonner";
import {
  ClipboardCheck, RefreshCw, CheckCircle2, XCircle, Filter, Loader2, Building2, User as UserIcon,
} from "lucide-react";
import { SortableHeader, useSortableRows } from "../components/SortableTable";

const STATUS_META = {
  pending:  { color: "#FF9F0A", label: "PENDING"  },
  approved: { color: "#34C759", label: "APPROVED" },
  rejected: { color: "#FF3B30", label: "REJECTED" },
};

/**
 * Ad-hoc Approvals — audit queue for shifts guards started themselves.
 * Approve-in-arrears: the shift already happened, this just records the
 * supervisor's after-the-fact call on whether it was legit.
 */
export default function AdhocApprovals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [status, setStatus] = useState("pending");
  const [autoOnly, setAutoOnly] = useState(false);
  const [decisionFor, setDecisionFor] = useState(null);   // { row, kind: 'approve' | 'reject' }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (status) params.status = status;
      const r = await api.get("/adhoc-approvals", { params });
      setRows(r.data || []);
    } catch (e) { toast.error(errorMessage(e, "Load failed")); }
    setLoading(false);
  }, [days, status]);
  useEffect(() => { load(); }, [load]);

  const accessors = useMemo(() => ({
    created_at: (r) => r.created_at || "",
    guard:      (r) => (r.guard_name_resolved || "").toLowerCase(),
    site:       (r) => (r.site_name || "").toLowerCase(),
    duration:   (r) => new Date(r.end_time).getTime() - new Date(r.start_time).getTime(),
    status:     (r) => r.approval_status || "",
  }), []);
  const sortState = useSortableRows(rows, accessors, {
    defaultKey: "created_at", defaultDir: "desc", storageKey: "adhoc-approvals",
  });
  const sorted = autoOnly ? sortState.sorted.filter((r) => r.auto_approved) : sortState.sorted;

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0 };
    for (const r of rows) c[r.approval_status] = (c[r.approval_status] || 0) + 1;
    return c;
  }, [rows]);

  const fmtHours = (start, end) => {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    const h = ms / 3_600_000;
    return h >= 1 ? `${h.toFixed(1)}h` : `${Math.round(h * 60)}m`;
  };

  return (
    <div>
      <PageHeader
        title="Ad-hoc Approvals"
        subtitle="Officer-initiated shifts pending supervisor sign-off"
        action={
          <GhostButton onClick={load} disabled={loading} data-testid="adhoc-refresh">
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </GhostButton>
        }
      />

      <div className="p-8 space-y-4">
        {/* Summary pills */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <div
              key={key}
              className="flex items-center gap-2 border border-[#27272A] bg-[#0F0F0F] px-3 py-1.5"
              data-testid={`adhoc-count-${key}`}
            >
              <span className="inline-block size-2.5 rounded-full" style={{ backgroundColor: meta.color }} />
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{meta.label}</span>
              <span className="font-mono font-black text-sm" style={{ color: meta.color }}>{counts[key] || 0}</span>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 border border-[#27272A] p-3">
          <div className="flex items-center gap-2">
            <Filter className="size-3.5 text-zinc-500" />
            <span className="label-mute">Filters</span>
          </div>
          <div>
            <div className="label-mute mb-1">Window</div>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="adhoc-days"
              className="bg-[#0A0A0A] border border-[#27272A] px-2 py-1 text-sm outline-none focus:border-[#FF3B30]"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </select>
          </div>
          <div>
            <div className="label-mute mb-1">Status</div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="adhoc-status-filter"
              className="bg-[#0A0A0A] border border-[#27272A] px-2 py-1 text-sm outline-none focus:border-[#FF3B30]"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <div className="label-mute mb-1">&nbsp;</div>
            <label className="flex items-center gap-2 cursor-pointer select-none border border-[#27272A] px-2 py-1 text-sm">
              <input
                type="checkbox"
                data-testid="adhoc-auto-only"
                className="accent-[#34C759]"
                checked={autoOnly}
                onChange={(e) => setAutoOnly(e.target.checked)}
              />
              <span className="text-zinc-300">Auto-approved only</span>
            </label>
          </div>
        </div>

        <div className="border border-[#27272A]">
          <table className="w-full text-sm" data-testid="adhoc-approvals-table">
            <thead className="bg-[#0F0F0F] border-b border-[#27272A]">
              <tr>
                <SortableHeader k="created_at" state={sortState} className="text-left px-3 py-2">Started</SortableHeader>
                <SortableHeader k="guard"      state={sortState} className="text-left px-3 py-2">Officer</SortableHeader>
                <SortableHeader k="site"       state={sortState} className="text-left px-3 py-2">Site</SortableHeader>
                <SortableHeader k="duration"   state={sortState} className="text-left px-3 py-2">Duration</SortableHeader>
                <SortableHeader k="status"     state={sortState} className="text-left px-3 py-2">Status</SortableHeader>
                <th className="text-left px-3 py-2">Decided</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-zinc-500">
                  {loading ? "Loading…" : "No ad-hoc shifts in this window."}
                </td></tr>
              )}
              {sorted.map((r) => {
                const smeta = STATUS_META[r.approval_status] || {};
                return (
                  <tr key={r.id} className="border-b border-[#1F1F1F] hover:bg-[#0F0F0F]" data-testid={`adhoc-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-400">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                      {r.location_verified === false && (
                        <div className="text-[10px] uppercase tracking-widest text-[#FF9F0A] mt-0.5">UNVERIFIED LOCATION</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <UserIcon className="size-3 text-zinc-500" />
                        <div>
                          <div className="font-display font-bold uppercase tracking-tight text-sm">{r.guard_name_resolved}</div>
                          {r.badge_no && <div className="text-[10px] text-zinc-500 font-mono">{r.badge_no}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 text-zinc-300">
                        <Building2 className="size-3 text-zinc-500" />
                        <div>
                          <div>{r.site_name || "—"}</div>
                          {r.client_name && <div className="text-[10px] text-zinc-500">{r.client_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono">{fmtHours(r.start_time, r.end_time)}</td>
                    <td className="px-3 py-2">
                      <PillBadge className="!text-[10px]" style={{ borderColor: smeta.color, color: smeta.color }}>
                        {smeta.label}
                      </PillBadge>
                      {r.auto_approved && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 border border-[#34C759]/60 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-[#34C759]"
                          data-testid={`adhoc-auto-badge-${r.id}`}
                        >
                          <CheckCircle2 className="size-2.5" /> Auto-approved
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-zinc-400">
                      {r.approval_decided_at ? (
                        <>
                          <div className="font-mono">{new Date(r.approval_decided_at).toLocaleString()}</div>
                          <div className="text-[10px] text-zinc-500">by {r.approval_decided_by_name || "—"}</div>
                          {r.approval_note && (
                            <div className="text-[10px] italic text-zinc-500 mt-0.5 max-w-[200px] truncate" title={r.approval_note}>
                              &ldquo;{r.approval_note}&rdquo;
                            </div>
                          )}
                        </>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.approval_status === "pending" && (
                        <div className="inline-flex items-center gap-2">
                          <GhostButton
                            data-testid={`adhoc-approve-${r.id}`}
                            onClick={() => setDecisionFor({ row: r, kind: "approve" })}
                            className="!border-[#34C759]/50 !text-[#34C759]"
                          >
                            <CheckCircle2 className="size-3" /> Approve
                          </GhostButton>
                          <GhostButton
                            data-testid={`adhoc-reject-${r.id}`}
                            onClick={() => setDecisionFor({ row: r, kind: "reject" })}
                            className="!border-[#FF3B30]/50 !text-[#FF3B30]"
                          >
                            <XCircle className="size-3" /> Reject
                          </GhostButton>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {decisionFor && (
        <DecisionModal
          row={decisionFor.row}
          kind={decisionFor.kind}
          onClose={() => setDecisionFor(null)}
          onDone={() => { setDecisionFor(null); load(); }}
        />
      )}
    </div>
  );
}


function DecisionModal({ row, kind, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const isReject = kind === "reject";
  const submit = async () => {
    if (isReject && !text.trim()) {
      toast.error("Rejection reason is required.");
      return;
    }
    setBusy(true);
    try {
      const path = `/adhoc-approvals/${row.id}/${kind}`;
      const body = isReject ? { reason: text.trim() } : { note: text.trim() || null };
      await api.post(path, body);
      toast.success(`Shift ${isReject ? "rejected" : "approved"} · ${row.guard_name_resolved}`);
      onDone();
    } catch (e) {
      toast.error(errorMessage(e, "Failed"));
    }
    setBusy(false);
  };
  const accent = isReject ? "#FF3B30" : "#34C759";
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="adhoc-decision-modal"
    >
      <div className="w-full max-w-md border bg-[#0F0F0F]" style={{ borderColor: `${accent}88` }} onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-[#27272A] px-5 py-4">
          <div className="label-mute" style={{ color: accent }}>
            {isReject ? "REJECT AD-HOC SHIFT" : "APPROVE AD-HOC SHIFT"}
          </div>
          <div className="font-display uppercase text-lg font-bold tracking-tight">
            {row.guard_name_resolved} · {row.site_name}
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="label-mute mb-1">
            {isReject ? "Reason (required)" : "Note (optional)"}
          </div>
          <textarea
            data-testid="adhoc-decision-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder={isReject ? "Why is this ad-hoc shift being rejected?" : "Optional context…"}
            className="w-full bg-[#0A0A0A] border border-[#27272A] text-sm p-2 focus:outline-none"
            style={{ borderColor: accent + "55" }}
            maxLength={500}
          />
          <div className="flex justify-end gap-2">
            <GhostButton onClick={onClose} disabled={busy}>Cancel</GhostButton>
            <GhostButton
              onClick={submit}
              disabled={busy}
              data-testid="adhoc-decision-submit"
              className="!border-2"
              style={{ borderColor: accent, color: accent }}
            >
              {busy ? <Loader2 className="size-3 animate-spin" /> : (isReject ? <XCircle className="size-3" /> : <CheckCircle2 className="size-3" />)}
              {isReject ? " Reject" : " Approve"}
            </GhostButton>
          </div>
        </div>
      </div>
    </div>
  );
}
